// Add these alongside your existing exports in services/catalogService.js
// (reuses the same `api` helper / authService.authenticatedFetch pattern).

import { API_BASE } from "../../../consts";
import authService from "../../../services/AuthService";

const api = (path, opts) => authService.authenticatedFetch(`${API_BASE}${path}`, opts);
const jsonOrThrow = async (resp) => {
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const err = new Error(data.message || data.error || `Request failed: ${resp.status}`);
        err.status = resp.status;
        err.code = data.error || null;
        err.detail = data.detail;
        err.itemErrors = data.item_errors || null;
        err.violations = data.violations || null;
        err.isWalmartInternalError = data.is_walmart_internal_error || false;
        err.correlationId = data.correlation_id || null;
        throw err;
    }
    return data;
};

// ── Sourcing ──
export const getEligibleWfsItems = (batchId) =>
    api(`/api/wfs/shipments/eligible-items${batchId ? `?batch_id=${batchId}` : ""}`).then(jsonOrThrow);

// Live fallback when neither a saved pack profile nor any past batch_items
// payload has dimensions for an item — same scrape-based lookup the
// listing wizard (MPItemFlow) already uses.
export const getWalmartInventoryBasic = (wpid) =>
    api(`/api/walmart/inventory-basic/${encodeURIComponent(wpid)}`).then(jsonOrThrow);

// Numeric walmart.com item ID (from the product URL) -> { match: { wpid, sku } } or { match: null }
export const resolveWalmartItemId = (itemId) =>
    api(`/api/wfs/shipments/resolve-item-id?q=${encodeURIComponent(itemId)}`).then(jsonOrThrow);

export const getPackProfile = (catalogItemId) =>
    api(`/api/wfs/shipments/pack-profile/${catalogItemId}`).then(jsonOrThrow);

export const updateItemPackDetails = (itemId, { weight, dimensions }) =>
    api(`/api/wfs/shipments/items/${itemId}/pack-details`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ weight, dimensions }),
    }).then(jsonOrThrow);

export const savePackProfile = (catalogItemId, profile) =>
    api(`/api/wfs/shipments/pack-profile/${catalogItemId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(profile),
    }).then(jsonOrThrow);

export const getReturnAddress = () => api("/api/settings/return-address").then(jsonOrThrow);
export const saveReturnAddress = (address) =>
    api("/api/settings/return-address", {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(address),
    }).then(jsonOrThrow);

// ── Draft / Preview / Create ──
export const createShipmentDraft = (payload) =>
    api("/api/wfs/shipments/draft", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
    }).then(jsonOrThrow);

export const fetchShipmentPreview = (shipmentId) =>
    api(`/api/wfs/shipments/${shipmentId}/preview`, { method: "POST" }).then(jsonOrThrow);

export const selectShipmentPreview = (shipmentId, previewSelected) =>
    api(`/api/wfs/shipments/${shipmentId}/select-preview`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ preview_selected: previewSelected }),
    }).then(jsonOrThrow);

export const createShipmentOnWalmart = (shipmentId) =>
    api(`/api/wfs/shipments/${shipmentId}/create`, { method: "POST" }).then(jsonOrThrow);

// ── List / detail / status ──
export const listWfsShipments = (status, page = 1, pageSize = 20, q = "", searchType = "all") => {
    const params = new URLSearchParams({ page, page_size: pageSize, search_type: searchType });
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    return api(`/api/wfs/shipments?${params}`).then(jsonOrThrow);
};

export const listExternalShipments = () => api("/api/wfs/shipments/external").then(jsonOrThrow);

export const importExternalShipment = (inboundOrderId) =>
    api(`/api/wfs/shipments/external/${encodeURIComponent(inboundOrderId)}/import`, { method: "POST" }).then(jsonOrThrow);

export const getWfsShipment = (shipmentId) => api(`/api/wfs/shipments/${shipmentId}`).then(jsonOrThrow);

export const refreshWfsShipmentStatus = (shipmentId) =>
    api(`/api/wfs/shipments/${shipmentId}/refresh-status`, { method: "POST" }).then(jsonOrThrow);

export const getWfsShipmentErrors = (shipmentId) => api(`/api/wfs/shipments/${shipmentId}/errors`).then(jsonOrThrow);

export const cancelWfsShipment = (shipmentId) =>
    api(`/api/wfs/shipments/${shipmentId}`, { method: "DELETE" }).then(jsonOrThrow);

export const enrichShipmentItemImage = (itemId) =>
    api(`/api/wfs/shipments/items/${itemId}/enrich`, { method: "POST" }).then(jsonOrThrow);

export const removeShipmentFromList = (shipmentId) =>
    api(`/api/wfs/shipments/${shipmentId}/remove-from-list`, { method: "DELETE" }).then(jsonOrThrow);

// ── Quantities ──
export const updateSplitQuantities = (splitId, updates) =>
    api(`/api/wfs/shipments/splits/${splitId}/quantities`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ updates }),
    }).then(jsonOrThrow);

// ── Labels ──
// `loadTypes` is an array of { loadType, count } groups (e.g. 2 mixed + 1
// single). Sent as ONE request so Walmart numbers every box in this call
// continuously ("1 of 3", "2 of 3", "3 of 3") instead of restarting the
// count per group — see backend receiving-label route.
export const fetchReceivingLabelBlob = async (splitId, { labelSize = "X4_6", labelFormat = "PDF", loadTypes = [{ loadType: "MIXED SKUS", count: 1 }] } = {}) => {
    const resp = await api(`/api/wfs/shipments/splits/${splitId}/receiving-label`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            label_size: labelSize, label_format: labelFormat,
            load_types: loadTypes.map((lt) => ({ load_type: lt.loadType, count: lt.count })),
        }),
    });
    if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || "Failed to generate receiving label");
    }
    return resp.blob();
};

// ── Tracking (self-arranged carrier) ──
export const updateSplitTracking = (splitId, carrierName, trackingNumbers) =>
    api(`/api/wfs/shipments/splits/${splitId}/tracking`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ carrier_name: carrierName, tracking_numbers: trackingNumbers }),
    }).then(jsonOrThrow);

// ── Box Configuration (physical boxes actually being shipped) ──
export const getBoxConfig = (splitId) =>
    api(`/api/wfs/shipments/splits/${splitId}/box-config`).then(jsonOrThrow);

export const saveBoxConfig = (splitId, boxes) =>
    api(`/api/wfs/shipments/splits/${splitId}/box-config`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ boxes }),
    }).then(jsonOrThrow);

// ── Carrier Rate Quote (Legacy) — Walmart-arranged carrier ──
export const createCarrierQuote = (splitId) =>
    api(`/api/wfs/shipments/splits/${splitId}/carrier-quote`, { method: "POST" }).then(jsonOrThrow);

export const getCarrierQuote = (splitId) =>
    api(`/api/wfs/shipments/splits/${splitId}/carrier-quote`).then(jsonOrThrow);

export const confirmCarrierQuote = (splitId) =>
    api(`/api/wfs/shipments/splits/${splitId}/carrier-quote/confirm`, { method: "POST" }).then(jsonOrThrow);

export const cancelCarrierQuote = (splitId) =>
    api(`/api/wfs/shipments/splits/${splitId}/carrier-quote`, { method: "DELETE" }).then(jsonOrThrow);

export const getBooking = (splitId) => api(`/api/wfs/shipments/splits/${splitId}/carrier-booking`).then(jsonOrThrow);

export const getPickupSchedule = (splitId) => api(`/api/wfs/shipments/splits/${splitId}/pickup-schedule`).then(jsonOrThrow);

export const getWpcTracking = (splitId) => api(`/api/wfs/shipments/splits/${splitId}/tracking/live`).then(jsonOrThrow);

export const fetchBolBlob = async (splitId) => {
    const resp = await api(`/api/wfs/shipments/splits/${splitId}/bol`);
    if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || "Failed to fetch Bill of Lading");
    }
    return resp.blob();
};

export const fetchCarrierLabelBlob = async (splitId, shipDate) => {
    const resp = await api(`/api/wfs/shipments/splits/${splitId}/carrier-label`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ship_date: shipDate }),
    });
    if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || "Failed to print carrier label");
    }
    return resp.blob();
};

// ── Prep report & checklist ──
export const getPrepReport = (shipmentId) => api(`/api/wfs/shipments/${shipmentId}/prep-report`).then(jsonOrThrow);

export const updateWizardStep = (shipmentId, step) =>
    api(`/api/wfs/shipments/${shipmentId}/step`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ step }),
    }).then(jsonOrThrow);

export const removeShipmentItem = (shipmentId, itemId) =>
    api(`/api/wfs/shipments/${shipmentId}/items/${itemId}`, { method: "DELETE" }).then(jsonOrThrow);

export const addDraftItems = (shipmentId, items) =>
    api(`/api/wfs/shipments/${shipmentId}/items`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
    }).then(jsonOrThrow);

export const updateShipmentReturnAddress = (shipmentId, address) =>
    api(`/api/wfs/shipments/${shipmentId}/return-address`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ return_address: address }),
    }).then(jsonOrThrow);

export const updateChecklistItem = (shipmentId, key, value) =>
    api(`/api/wfs/shipments/${shipmentId}/checklist`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ key, value }),
    }).then(jsonOrThrow);

export const createCustomSplitShipment = (shipmentId, boxes) =>
    api(`/api/wfs/shipments/${shipmentId}/create-custom-split`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ boxes }),
    }).then(jsonOrThrow);

// Build Your Own Boxes: asks Walmart's preview how it will really split this
// exact box plan, without creating anything.
export const previewCustomSplit = (shipmentId, boxes) =>
    api(`/api/wfs/shipments/${shipmentId}/custom-split-preview`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ boxes }),
    }).then(jsonOrThrow);

export const getSelfMirroringDestinations = (shipmentId) =>
    api(`/api/wfs/shipments/${shipmentId}/self-mirroring-destinations`).then(jsonOrThrow);